import { useFrame } from '@react-three/fiber'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { RolodexTilesPlugin } from 'features/overview'
import {
  GLTFExtensionsPlugin,
  ReorientationPlugin,
  TilesPluginR3f,
  TilesRendererR3f,
} from 'gecko-3d-tiles-renderer'
import { MeshoptDecoder } from 'meshoptimizer'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Group, Material, Mesh, Object3D } from 'three'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'

const DRACO_DECODER_PATH =
  'https://www.gstatic.com/draco/versioned/decoders/1.5.7/'
const dracoLoader = new DRACOLoader().setDecoderPath(DRACO_DECODER_PATH)

interface SingleTilesetProps {
  kestrelModelId: string
  /** Path relative to 3d_tiles folder (e.g., 'rgb' or 'rgb/BlockA') */
  tilesetPath: string
  visible: boolean
  opacity?: number
  onLoaded?: () => void
  onRendererReady?: (renderer: unknown) => void
}

interface TilesLayerProps {
  kestrelModelId: string
  /** Paths to tileset folders (e.g., ['rgb'] or ['rgb/BlockA', 'rgb/BlockB']) */
  tilesetPaths: string[]
  visible: boolean
  opacity?: number
  onLoaded?: () => void
  /** Called when each renderer is ready, with the renderer and its index */
  onRendererReady?: (renderer: unknown, index: number) => void
}

/**
 * Set opacity on all materials in an object tree
 */
function setTreeOpacity(object: Object3D, opacity: number): void {
  object.traverse((child) => {
    if (child instanceof Mesh && child.material) {
      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material]
      materials.forEach((mat: Material) => {
        if ('opacity' in mat) {
          mat.opacity = opacity
          mat.transparent = opacity < 1
          mat.needsUpdate = true
        }
      })
    }
  })
}

/**
 * Renders a single tileset from a specific path.
 */
const SingleTileset: FC<SingleTilesetProps> = ({
  kestrelModelId,
  tilesetPath,
  visible,
  opacity = 1,
  onLoaded,
  onRendererReady,
}) => {
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()
  const [tilesRenderer, setTilesRenderer] = useState<unknown>(null)
  const [decoderReady, setDecoderReady] = useState(false)
  const hasCalledOnLoaded = useRef(false)

  useEffect(() => {
    MeshoptDecoder.ready.then(() => {
      setDecoderReady(true)
    })
  }, [])

  const rolodexPluginOptions = useMemo(() => {
    const rootPath = `3d_tiles/${tilesetPath}`
    return {
      entryID: kestrelModelId,
      rootPath: rootPath,
      axios: authorizedAxios,
      rolodexURL: ROLODEX_URL,
    }
  }, [ROLODEX_URL, authorizedAxios, kestrelModelId, tilesetPath])

  const reorientationOptions = useMemo(
    () => ({
      up: '-y' as const,
      recenter: false,
    }),
    []
  )

  // Store current opacity in ref for event handlers
  const opacityRef = useRef(opacity)
  opacityRef.current = opacity

  // Handle tiles-load-end event and apply opacity to newly loaded tiles
  useEffect(() => {
    if (!tilesRenderer) return

    const renderer = tilesRenderer as {
      group?: Group
      addEventListener?: (
        event: string,
        callback: (evt: { scene?: Object3D }) => void
      ) => void
      removeEventListener?: (
        event: string,
        callback: (evt: { scene?: Object3D }) => void
      ) => void
    }

    // Apply opacity when any model/tile is loaded
    const handleTileLoad = (evt: { scene?: Object3D }) => {
      if (evt.scene) {
        setTreeOpacity(evt.scene, opacityRef.current)
      }
      // Also apply to entire group to catch any missed tiles
      if (renderer.group) {
        setTreeOpacity(renderer.group, opacityRef.current)
      }
    }

    // Handle initial load complete
    const handleLoadEnd = () => {
      if (!hasCalledOnLoaded.current) {
        hasCalledOnLoaded.current = true
        onLoaded?.()
      }
    }

    if (renderer.addEventListener) {
      renderer.addEventListener('load-model', handleTileLoad)
      renderer.addEventListener('tiles-load-end', handleLoadEnd)
    }

    return () => {
      if (renderer.removeEventListener) {
        renderer.removeEventListener('load-model', handleTileLoad)
        renderer.removeEventListener('tiles-load-end', handleLoadEnd)
      }
    }
  }, [tilesRenderer, onLoaded])

  // Control visibility of the tiles group
  useEffect(() => {
    if (!tilesRenderer) return
    const renderer = tilesRenderer as { group?: Group }
    if (renderer.group) {
      renderer.group.visible = visible
    }
  }, [tilesRenderer, visible])

  // Apply opacity immediately when it changes
  useEffect(() => {
    if (!tilesRenderer) return
    const renderer = tilesRenderer as { group?: Group }
    if (renderer.group) {
      setTreeOpacity(renderer.group, opacity)
    }
  }, [tilesRenderer, opacity])

  // Also continuously apply opacity in useFrame as backup
  useFrame(() => {
    if (!tilesRenderer) return
    const renderer = tilesRenderer as { group?: Group }
    if (renderer.group) {
      setTreeOpacity(renderer.group, opacityRef.current)
    }
  })

  if (!decoderReady) {
    return null
  }

  return (
    <TilesRendererR3f
      url={'tileset.json'}
      errorTarget={0.5}
      ref={(ref) => {
        setTilesRenderer(ref)
        onRendererReady?.(ref)
      }}>
      <TilesPluginR3f plugin={RolodexTilesPlugin} args={rolodexPluginOptions} />
      <TilesPluginR3f
        plugin={ReorientationPlugin}
        args={reorientationOptions}
      />
      <TilesPluginR3f
        plugin={GLTFExtensionsPlugin}
        args={{ meshoptDecoder: MeshoptDecoder, dracoLoader }}
      />
    </TilesRendererR3f>
  )
}

/**
 * Renders one or more 3D tilesets for a single layer.
 * Handles both simple case (single tileset) and nested subfolders (multiple tilesets).
 */
export const TilesLayer: FC<TilesLayerProps> = ({
  kestrelModelId,
  tilesetPaths,
  visible,
  opacity = 1,
  onLoaded,
  onRendererReady,
}) => {
  const loadedCount = useRef(0)
  const totalTilesets = tilesetPaths.length
  const hasCalledOnLoaded = useRef(false)

  const handleTilesetLoaded = () => {
    loadedCount.current += 1
    if (loadedCount.current >= totalTilesets && !hasCalledOnLoaded.current) {
      hasCalledOnLoaded.current = true
      onLoaded?.()
    }
  }

  // Reset counters when tilesetPaths change
  useEffect(() => {
    loadedCount.current = 0
    hasCalledOnLoaded.current = false
  }, [tilesetPaths])

  return (
    <>
      {tilesetPaths.map((tilesetPath, index) => (
        <SingleTileset
          key={tilesetPath}
          kestrelModelId={kestrelModelId}
          tilesetPath={tilesetPath}
          visible={visible}
          opacity={opacity}
          onLoaded={handleTilesetLoaded}
          onRendererReady={(renderer) => onRendererReady?.(renderer, index)}
        />
      ))}
    </>
  )
}
